import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators} from '@angular/forms';
import {SelectItem} from 'primeng/api';
import {AutoCompleteSelectEvent} from 'primeng/autocomplete';
import {EMPTY, Subject, Subscription} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, filter, switchMap} from 'rxjs/operators';
import {ModalInterface} from '../../modal.interface';
import {ModalComponent} from '../../modal.component';
import {ModalState} from '../../modal-state.enum';
import {ModalStateEvent} from '../../modal-state-event';
import {PlaceCategoryApiModel} from '../../../../api/models/place-category.api-model';
import {PlaceApiModel} from '../../../../api/models/place.api-model';
import {PlaceRestService, PlaceTranslationSuggestion} from '../../../../api/services/place.rest.service';
import {MapComponent} from '../../../map/map.component';
import {MapService} from '../../../map/map.service';
import {PlaceLabelService} from '../../../../features/place/place-label.service';
import {TranslateService} from '@ngx-translate/core';

function selectedAddressValidator(control: AbstractControl): ValidationErrors | null {
  const address = control.value?.value;
  const latitude = Number(address?.lat);
  const longitude = Number(address?.lon);

  return Number.isFinite(latitude) && Number.isFinite(longitude)
    && latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180
    ? null
    : {addressSelection: true};
}

@Component({selector: 'app-add-place', templateUrl: './add-place.component.html', styleUrls: ['./add-place.component.scss']})
export class AddPlaceComponent implements ModalInterface, OnInit, OnDestroy {
  public data: {categories?: PlaceCategoryApiModel[]; place?: PlaceApiModel};
  public parent?: ModalComponent;
  public subject: Subject<ModalStateEvent>;
  public state: any;
  public form: UntypedFormGroup;
  public categories: PlaceCategoryApiModel[] = [];
  public searchResults: SelectItem[] = [];
  public initialCenter = {lat: 52.069, lon: 19.48};
  public initialMarker?: {lat: number; lon: number};
  public submitError = '';
  public translationSuggestionError = '';
  public translationSuggestionLoading = false;
  public sourceTranslationLocale?: 'pl-PL'|'en-US'|'de-DE';
  public submitAttempted = false;
  private subscriptions: Subscription[] = [];
  private lastSuggestedTranslations?: Record<'pl-PL'|'en-US'|'de-DE', string>;

  @ViewChild(MapComponent) private mapComponent?: MapComponent;

  constructor(
    private readonly placesApi: PlaceRestService,
    private readonly mapService: MapService,
    public readonly placeLabels: PlaceLabelService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    // The app locale is the reliable source-language declaration. In particular,
    // short place names are not dependable enough for language detection alone.
    this.sourceTranslationLocale = this.toSupportedLocale(this.translate.currentLang || this.translate.getDefaultLang());
    this.categories = this.data?.categories || [];
    this.form = new UntypedFormGroup({
      name: new UntypedFormControl('', [Validators.required, Validators.pattern(/.*\S.*/)]),
      category: new UntypedFormControl(null, Validators.required),
      address: new UntypedFormControl(null, [Validators.required, selectedAddressValidator]),
      description: new UntypedFormControl(''),
      accessNote: new UntypedFormControl(''),
      nameTranslations: new UntypedFormGroup({
        'pl-PL': new UntypedFormControl(''),
        'en-US': new UntypedFormControl(''),
        'de-DE': new UntypedFormControl(''),
      }),
    });
    if (this.data?.place) {
      const place = this.data.place;
      const coordinates = place.address?.coordinates;
      const addressValue = coordinates ? {label: place.address?.place || `${coordinates[1]}, ${coordinates[0]}`, value: {display_name: place.address?.place || '', lat: coordinates[1], lon: coordinates[0]}} : null;
      this.form.patchValue({name: place.name, category: place.category?.id, address: addressValue, description: place.description, accessNote: place.accessNote || '', nameTranslations: place.nameTranslations || {}});
      if (coordinates?.length === 2) {
        this.initialCenter = {lat: coordinates[1], lon: coordinates[0]};
        this.initialMarker = this.initialCenter;
        this.searchResults = addressValue ? [addressValue] : [];
      }
    }
    if (!this.categories.length) {
      this.subscriptions.push(this.placesApi.getCategories().subscribe(categories => this.categories = categories));
    }
    this.subscriptions.push(this.form.statusChanges.subscribe(() => {
      // The shared modal disables its action while a request is pending. Once
      // all mandatory fields are valid, make that action available again.
      if (this.form.valid) {
        this.subject.next(new ModalStateEvent(ModalState.VALID));
      }
    }));
    this.subscriptions.push(this.form.get('name')!.valueChanges.pipe(
      debounceTime(700),
      distinctUntilChanged(),
      filter((name: string) => typeof name === 'string' && name.trim().length > 0),
      switchMap((name: string) => {
        this.resetOnlyUnapprovedSuggestions();
        this.translationSuggestionLoading = true;
        this.translationSuggestionError = '';
        return this.placesApi.suggestNameTranslations(name.trim(), this.sourceTranslationLocale).pipe(catchError(() => {
          this.translationSuggestionLoading = false;
          this.translationSuggestionError = 'places.add.translation_unavailable';
          return EMPTY;
        }));
      }),
    ).subscribe(suggestion => this.applyTranslationSuggestion(suggestion)));
    this.subscriptions.push(this.state.subscribe((event: ModalStateEvent) => this.handleModalState(event)));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  get childCategories(): PlaceCategoryApiModel[] {
    return this.categories.filter(category => !!category.parentSlug);
  }

  get childCategoryOptions(): Array<{label: string; value: number}> {
    return this.childCategories.map(category => ({label: this.placeLabels.category(category), value: category.id}));
  }

  public isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control?.invalid && (this.submitAttempted || control.touched);
  }

  searchPlaces(event: {query: string}): void {
    const subscription = this.mapService.subscribeToQuerySubject().subscribe((results: any[]) => {
      this.searchResults = results.map(result => ({label: result.display_name, value: result}));
      subscription.unsubscribe();
    });
    this.mapService.searchPlaces(event.query);
  }

  selectPlace(event: AutoCompleteSelectEvent): void {
    const item = event.value?.value;
    if (!item || !Number.isFinite(Number(item.lat)) || !Number.isFinite(Number(item.lon))) {
      return;
    }
    const marker = {lat: Number(item.lat), lon: Number(item.lon)};
    this.initialCenter = marker;
    this.initialMarker = marker;
    this.mapComponent?.updateMapCenter(marker);
    this.mapComponent?.waitUntilReady();
    this.form.get('address')?.updateValueAndValidity();
  }

  selectMapPoint(point: {lat: number; lon: number}): void {
    this.initialCenter = point;
    this.initialMarker = point;
    this.mapComponent?.updateMapCenter(point);
    this.mapComponent?.waitUntilReady();
    const selectedAddress = {label: `${point.lat}, ${point.lon}`, value: {lat: point.lat, lon: point.lon, display_name: ''}};
    this.searchResults = [selectedAddress];
    this.form.patchValue({address: selectedAddress});
    this.subscriptions.push(this.mapService.reverseGeocode(point.lat, point.lon).subscribe({
      next: result => {
        const value = result?.display_name ? result : {...result, lat: point.lat, lon: point.lon};
        const address = {label: value.display_name || `${point.lat}, ${point.lon}`, value};
        this.searchResults = [address];
        this.form.patchValue({address});
        this.form.get('address')?.setErrors(null);
      },
    }));
  }

  private handleModalState(event: ModalStateEvent): void {
    if (event.status !== ModalState.PENDING) {
      return;
    }
    if (this.form.invalid) {
      this.submitAttempted = true;
      this.form.markAllAsTouched();
      // Do not leave the global modal action permanently disabled after a
      // client-side validation failure. The user can correct the fields and
      // submit again immediately.
      this.subject.next(new ModalStateEvent(ModalState.VALID));
      return;
    }
    this.submitAttempted = false;
    this.submitError = '';
    const address = this.form.get('address')?.value?.value;
    const payload = {
      ...this.form.getRawValue(),
      formattedAddress: address?.display_name || '',
      latitude: Number(address?.lat),
      longitude: Number(address?.lon),
    };
    delete payload.address;
    const request = this.data?.place ? this.placesApi.resubmit(this.data.place.slug, payload) : this.placesApi.create(payload);
    this.subscriptions.push(request.subscribe({
      next: place => this.subject.next(new ModalStateEvent(ModalState.SUCCESS, place)),
      error: error => {
        this.submitError = error.status === 409 ? 'places.add.duplicate' : 'places.add.error';
        // Preserve entered values and allow a retry after all server errors.
        this.subject.next(new ModalStateEvent(ModalState.VALID));
      },
    }));
  }

  private applyTranslationSuggestion(suggestion: PlaceTranslationSuggestion): void {
    this.translationSuggestionLoading = false;
    // Keep the explicitly selected application language authoritative. The
    // detection result is only a fallback for an unsupported application locale.
    this.sourceTranslationLocale = this.sourceTranslationLocale || this.toSupportedLocale(suggestion.sourceLanguage);
    const translations = this.form.get('nameTranslations');
    if (!translations) {
      return;
    }
    const current = translations.getRawValue();
    const accepted: Record<string, string> = {};
    Object.keys(suggestion.translations).forEach(locale => {
      accepted[locale] = current[locale] || suggestion.translations[locale as keyof PlaceTranslationSuggestion['translations']];
    });
    translations.patchValue(accepted);
    this.lastSuggestedTranslations = suggestion.translations;
  }

  private resetOnlyUnapprovedSuggestions(): void {
    if (!this.lastSuggestedTranslations) {
      return;
    }
    const translations = this.form.get('nameTranslations');
    const current = translations?.getRawValue();
    if (!translations || !current || Object.keys(this.lastSuggestedTranslations).some(locale => current[locale] !== this.lastSuggestedTranslations![locale])) {
      return;
    }
    translations.reset({}, {emitEvent: false});
    this.lastSuggestedTranslations = undefined;
  }

  private toSupportedLocale(language?: string): 'pl-PL'|'en-US'|'de-DE'|undefined {
    const code = (language || '').toLowerCase().split('-')[0];
    return ({pl: 'pl-PL', en: 'en-US', de: 'de-DE'} as const)[code];
  }
}
